/**
 * ScormPlayer — Sprint 30 SCORM Player với xAPI integration.
 *
 * Architecture:
 *   1. Fetch signed launcher_url từ /api/integration/scorm/session/<id>/
 *   2. Render <iframe src={launcher_url}> — Django serves the launcher HTML
 *   3. Launcher HTML defines window.API_1484_11 shim, loads SCO in sub-iframe
 *   4. Shim posts messages to this React parent:
 *        {source:"sep2026-scorm", type:"scorm.init"|"scorm.commit"|"scorm.complete"}
 *   5. On "scorm.complete" → show completion badge
 *
 * Cross-origin note:
 *   Dev (Vite :5173, Django :8000): postMessage crosses origins — works.
 *   Prod (both behind same Nginx): same-origin — also works.
 *   The sub-iframe for SCO content + launcher are always same-origin (Django).
 */

import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Result, Spin, Typography } from "antd";
import { CheckCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { useScormSession } from "@/api/integration";

const { Text } = Typography;

interface ScormMessage {
  source: "sep2026-scorm";
  pkg_id: number;
  type: "scorm.init" | "scorm.commit" | "scorm.terminate" | "scorm.complete";
  completion_status?: string;
  score_scaled?: number | null;
}

interface Props {
  packageId: number;
  title?: string;
  height?: number | string;
}

export function ScormPlayer({ packageId, title, height = "70vh" }: Props) {
  const { data: session, isLoading, error } = useScormSession(packageId);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [scormStatus, setScormStatus] = useState<{
    initialized: boolean;
    completed: boolean;
    score: number | null;
  }>({ initialized: false, completed: false, score: null });

  // Listen for postMessages from the SCORM launcher iframe
  useEffect(() => {
    function handleMessage(ev: MessageEvent) {
      const msg = ev.data as ScormMessage;
      if (!msg || msg.source !== "sep2026-scorm") return;
      if (msg.pkg_id !== packageId) return;

      if (msg.type === "scorm.init") {
        setScormStatus((s) => ({ ...s, initialized: true }));
      }

      if (msg.type === "scorm.commit" || msg.type === "scorm.terminate") {
        if (msg.score_scaled != null) {
          setScormStatus((s) => ({ ...s, score: msg.score_scaled ?? null }));
        }
      }

      if (msg.type === "scorm.complete") {
        setScormStatus({ initialized: true, completed: true, score: msg.score_scaled ?? null });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [packageId]);

  if (isLoading) {
    return (
      <div
        style={{ textAlign: "center", padding: 64, background: "#0d1117", borderRadius: 8 }}
        data-testid="scorm-loading"
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: "#fff" }} />} />
        <div style={{ color: "#7a8499", marginTop: 12 }}>Đang tải SCORM content...</div>
      </div>
    );
  }

  if (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      return (
        <Alert
          type="warning"
          showIcon
          message="Cần đăng ký môn học để học nội dung SCORM này."
          data-testid="scorm-not-enrolled"
        />
      );
    }
    return (
      <Result
        status="error"
        title="Không tải được SCORM content"
        subTitle={String(error)}
        data-testid="scorm-error"
      />
    );
  }

  if (!session) return null;

  const displayTitle = title || session.title;

  return (
    <div data-testid="scorm-player">
      {/* Status bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 8,
          padding: "8px 12px",
          background: "#0d1117",
          borderRadius: "8px 8px 0 0",
          color: "#e6edf3",
        }}
      >
        <Text style={{ color: "#e6edf3", fontWeight: 600, flex: 1 }} ellipsis>
          {displayTitle}
        </Text>

        {scormStatus.completed ? (
          <Badge
            status="success"
            text={
              <span style={{ color: "#3fb950" }}>
                <CheckCircleOutlined style={{ marginRight: 4 }} />
                Hoàn thành
                {scormStatus.score != null && (
                  <span style={{ marginLeft: 8, color: "#8b949e" }}>
                    ({Math.round(scormStatus.score * 100)}%)
                  </span>
                )}
              </span>
            }
          />
        ) : scormStatus.initialized ? (
          <Badge status="processing" text={<span style={{ color: "#58a6ff" }}>Đang học...</span>} />
        ) : (
          <Badge status="default" text={<span style={{ color: "#8b949e" }}>Đang khởi tạo</span>} />
        )}
      </div>

      {/* SCORM iframe */}
      <iframe
        ref={iframeRef}
        src={session.launcher_url}
        title={displayTitle}
        style={{
          display: "block",
          width: "100%",
          height,
          border: "none",
          borderRadius: "0 0 8px 8px",
          background: "#000",
        }}
        allow="autoplay; fullscreen"
        data-testid="scorm-iframe"
      />
    </div>
  );
}
